import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormHelperText,
  Chip,
  Box,
  IconButton,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { makeStyles } from "tss-react/mui";
import CloseIcon from "@mui/icons-material/Close";

const useStyles = makeStyles()((theme, _params, classes) => ({
  gradientText: {
    background: theme.palette.accentGradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  tagsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
}));

function EditTagsDialog({ file, handleUpdateFile, onClose }) {
  const { classes } = useStyles();
  const [tags, setTags] = useState(
    file["user_tags"]
      ? file["user_tags"].split(",").map((tag) => tag.trim())
      : []
  );

  const validationSchema = yup.object({
    currentTag: yup
      .string()
      .trim()
      .test("no-duplicate", "This tag already exists", function (value) {
        if (!value) return true;
        return !tags.includes(value.trim());
      }),
  });

  const formik = useFormik({
    initialValues: {
      currentTag: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      if (values.currentTag.trim()) {
        const newTags = [...tags, values.currentTag.trim()];
        setTags(newTags);
        resetForm();
      }
    },
  });

  const handleDeleteTag = (tagToDelete) => {
    setTags(tags.filter((tag) => tag !== tagToDelete));
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && formik.values.currentTag.trim()) {
      event.preventDefault();
      formik.handleSubmit();
    }
  };

  const handleSave = () => {
    handleUpdateFile({ id: file["id"], user_tags: tags.join(",") });
    onClose();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      PaperProps={{
        style: {
          maxWidth: "sm",
        },
      }}>
      <DialogTitle variant="h6" sx={{ display: "flex", alignItems: "center" }}>
        Edit Tags
      </DialogTitle>
      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <TextField
            fullWidth
            sx={{ marginTop: 1 }}
            variant="outlined"
            label="Add Tag"
            name="currentTag"
            error={Boolean(formik.errors.currentTag)}
            value={formik.values.currentTag}
            onChange={formik.handleChange}
            onKeyPress={handleKeyPress}
            placeholder="Type and press Enter to add a tag"
          />
          {formik.errors.currentTag && (
            <FormHelperText error sx={{ ml: 1 }}>
              {formik.errors.currentTag}
            </FormHelperText>
          )}

          <Box className={classes.tagsContainer} mt={2}>
            {tags.map((tag) => (
              <Chip
                size="small"
                key={tag}
                label={tag}
                onDelete={() => handleDeleteTag(tag)}
                deleteIcon={<CloseIcon />}
              />
            ))}
          </Box>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          <span className={classes.gradientText}>Cancel</span>
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditTagsDialog;
